/**
 * Store for managing error details drawer state.
 *
 * Allows any component to show detailed error information in a drawer
 * by setting the current error.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { ApiError } from '@/util/ApiError'

/** An ID from outside Reach that the operator gives to that system's support, such as Avalara's. */
export interface ErrorReference {
  label: string
  value: string
  hint?: string
}

export const useErrorDetailsStore = defineStore('errorDetails', () => {
  // State
  const currentError = ref<ApiError | null>(null)
  const isDrawerVisible = ref(false)
  const errorTitle = ref<string>('Error Details')
  const references = ref<ErrorReference[]>([])

  // Actions
  /**
   * Show the error details drawer with the given error.
   * @param error - The ApiError to display
   * @param title - Optional custom title for the drawer
   * @param refs - Optional external IDs, each shown with a copy control
   */
  function showErrorDetails(
    error: ApiError,
    title?: string,
    refs: ErrorReference[] = [],
  ) {
    currentError.value = error
    errorTitle.value = title ?? 'Error Details'
    references.value = refs
    isDrawerVisible.value = true
  }

  /**
   * Hide the error details drawer.
   */
  function hideErrorDetails() {
    isDrawerVisible.value = false
    // Clear error after animation completes
    setTimeout(() => {
      if (!isDrawerVisible.value) {
        currentError.value = null
        errorTitle.value = 'Error Details'
        references.value = []
      }
    }, 300)
  }

  /**
   * Toggle drawer visibility.
   */
  function toggleDrawer() {
    if (isDrawerVisible.value) {
      hideErrorDetails()
    } else if (currentError.value) {
      isDrawerVisible.value = true
    }
  }

  return {
    // State
    currentError,
    isDrawerVisible,
    errorTitle,
    references,

    // Actions
    showErrorDetails,
    hideErrorDetails,
    toggleDrawer,
  }
})
